import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AddEmployeeDialog,
  ColumnConfig,
  DEFAULT_FILTERS,
  EmployeeCardGrid,
  EmployeeTable,
  ExportButton,
  FilterSidebar,
  Pagination,
  StatusCards,
  useColumnVisibility,
  type EmployeeFilters,
} from '@/features/people/components';
import { useFilterOptions } from '@/components/filters';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { usePermission, useCanViewCompensations } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import {
  employeesKeys,
  useEmployeeStats,
  useEmployees,
  useInfiniteEmployees,
} from '@/hooks/use-employees';
import { deleteEmployee, reactivateEmployee } from '@/lib/employees';
import { ReassignReportsDialog } from '@/features/people/components/reassign-reports-dialog';
import {
  deactivateWithReportHandoff,
  type ReportReassignment,
  type UnsettledDirectReport,
} from '@/features/people/lib/deactivation-reports';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import type { EmployeeQueryParams } from '@/types/employee';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Filter, LayoutGrid, List, Search, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { toast } from 'sonner';

type ViewMode = 'list' | 'cards';

const VIEW_MODE_STORAGE_KEY = 'employee-view-mode';

function getStoredViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'list';
  const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return stored === 'cards' ? 'cards' : 'list';
}

export const Route = createFileRoute('/people')({
  component: PeoplePage,
});

function PeoplePage() {
  const formatName = useEmployeeNameFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<EmployeeFilters>(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const { visibleColumns, setVisibleColumns } = useColumnVisibility();
  const debouncedSearch = useDebouncedValue(search, 300);
  const limit = viewMode === 'list' ? 24 : 20; // Smaller page for cards

  // Prefetch filter-sidebar option lists so the panel renders fully populated
  // from the first frame of its slide-in animation. Previously these queries
  // fired only when the sheet content mounted (Radix Portal lazy-mounts on
  // open), so selects would briefly show loading skeletons mid-animation and
  // then snap to data — visible as a "jerk" right after the sheet appeared.
  // The hooks are cheap (TanStack Query caches indefinitely with the default
  // staleTime, and most of these are already fetched by other parts of the
  // app — departments/positions are tiny lists).
  useFilterOptions('departments');
  useFilterOptions('positions');
  useFilterOptions('managers');
  useFilterOptions('employmentTypes');
  useFilterOptions('seniorityLevels');
  const [deactivateDialog, setDeactivateDialog] = useState<{
    open: boolean;
    employeeId: string | null;
    employeeName: string;
    isReactivate: boolean;
  }>({ open: false, employeeId: null, employeeName: '', isReactivate: false });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    employeeId: string | null;
    employeeName: string;
  }>({ open: false, employeeId: null, employeeName: '' });
  // Set when the backend refuses a deactivation because the employee still has
  // direct reports — carries the list straight into the reassignment dialog.
  const [reportHandoff, setReportHandoff] = useState<{
    employeeId: string;
    employeeName: string;
    reports: UnsettledDirectReport[];
  } | null>(null);
  const [isHandingOff, setIsHandingOff] = useState(false);
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const canCreateEmployee = usePermission('employees.profile.create');
  const canEditEmployee = usePermission('employees.profile.edit');
  const canDeleteEmployee = usePermission('employees.profile.delete');
  const canImpersonate = usePermission('auth.impersonate');
  const canViewCompensations = useCanViewCompensations();
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);

  const handleDeactivate = (id: string) => {
    const employee =
      listEmployees.find((e) => e.id === id) || cardEmployees.find((e) => e.id === id);
    const employeeName = employee ? formatName({ firstName: employee.firstName, lastName: employee.lastName }) : 'this employee';
    const isTerminated = employee?.status === 'terminated';

    setDeactivateDialog({
      open: true,
      employeeId: id,
      employeeName,
      isReactivate: isTerminated,
    });
  };

  const handleDelete = (id: string) => {
    const employee =
      listEmployees.find((e) => e.id === id) || cardEmployees.find((e) => e.id === id);
    const employeeName = employee ? formatName({ firstName: employee.firstName, lastName: employee.lastName }) : 'this employee';

    setDeleteDialog({
      open: true,
      employeeId: id,
      employeeName,
    });
  };

  const handleImpersonate = async (id: string) => {
    try {
      await startImpersonation(id);
      navigate({ to: '/' });
    } catch (err) {
      // The server says why (e.g. already impersonating); the generic text is
      // only for when it says nothing. [HW-942]
      toast.error(extractErrorMessage(err, 'Failed to impersonate employee. Please try again.'));
    }
  };

  const afterStatusChange = async () => {
    // Invalidate all employee queries to refresh the list
    await queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    // Also invalidate stats
    await queryClient.invalidateQueries({ queryKey: employeesKeys.stats() });

    // If we're on a page that might now be empty, go back to page 1
    if (viewMode === 'list' && listEmployees.length === 1 && page > 1) {
      setPage(1);
    }

    setDeactivateDialog({ open: false, employeeId: null, employeeName: '', isReactivate: false });
  };

  const confirmDeactivate = async () => {
    if (!deactivateDialog.employeeId) return;
    const { employeeId, employeeName, isReactivate } = deactivateDialog;

    try {
      if (isReactivate) {
        await reactivateEmployee(employeeId);
      } else {
        const result = await deactivateWithReportHandoff(employeeId);
        if (!result.ok) {
          // Hand over to the reassignment dialog; nothing has changed yet.
          setDeactivateDialog({
            open: false,
            employeeId: null,
            employeeName: '',
            isReactivate: false,
          });
          setReportHandoff({ employeeId, employeeName, reports: result.reports });
          return;
        }
      }
      await afterStatusChange();
    } catch (error) {
      // The refusals carry their reason — MANAGER_NO_LONGER_ACTIVE, a malformed
      // handover, a missing permission — and swallowing them would leave the
      // operator retrying the same rejected request.
      toast.error(
        extractErrorMessage(
          error,
          `Failed to ${isReactivate ? 'reactivate' : 'deactivate'} employee. Please try again.`
        )
      );
    }
  };

  const confirmReportHandoff = async (assignments: ReportReassignment[]) => {
    if (!reportHandoff) return;
    setIsHandingOff(true);
    try {
      const result = await deactivateWithReportHandoff(
        reportHandoff.employeeId,
        undefined,
        assignments
      );
      if (!result.ok) {
        // Someone gained a report while the dialog was open — ask again.
        setReportHandoff({ ...reportHandoff, reports: result.reports });
        toast.error('The list of direct reports changed. Please review it again.');
        return;
      }
      setReportHandoff(null);
      await afterStatusChange();
    } catch (error) {
      toast.error(
        extractErrorMessage(error, 'Failed to deactivate employee. Please try again.')
      );
    } finally {
      setIsHandingOff(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.employeeId) return;

    try {
      await deleteEmployee(deleteDialog.employeeId);
      // Invalidate all employee queries to refresh the list
      await queryClient.invalidateQueries({ queryKey: employeesKeys.all });
      // Also invalidate stats
      await queryClient.invalidateQueries({ queryKey: employeesKeys.stats() });

      // If we're on a page that might now be empty, go back to page 1
      if (viewMode === 'list' && listEmployees.length === 1 && page > 1) {
        setPage(1);
      }

      setDeleteDialog({ open: false, employeeId: null, employeeName: '' });
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete employee. Please try again.'));
    }
  };

  // Build query params from filters (without page for infinite).
  //
  // Search HONORS the current status filter, it doesn't bypass it. Previously
  // typing in the search box silently wiped `status` / `statuses` so every
  // query reached across all employees including terminated — which made
  // searching for "Anton" surface ex-employees alongside current staff. Now
  // the search obeys whatever status the user has picked (default
  // `current_staff` = active + probation), and they have to explicitly switch
  // the Status chip / sidebar to `All` to reach terminated employees.
  const baseQueryParams: Omit<EmployeeQueryParams, 'page'> = {
    limit,
    search: debouncedSearch || undefined,
    ...(filters.status === 'current_staff'
      ? { statuses: ['active', 'probation'], status: undefined }
      : filters.status !== 'all'
        ? { status: filters.status, statuses: undefined }
        : { status: undefined, statuses: undefined }),
    departmentId: filters.departmentId !== 'all' ? filters.departmentId : undefined,
    // Use positionIds if set (for category filtering), otherwise use positionId
    positionIds:
      filters.positionIds && filters.positionIds.length > 0 ? filters.positionIds : undefined,
    positionId:
      !filters.positionIds && filters.positionId !== 'all' ? filters.positionId : undefined,
    managerId: filters.managerId !== 'all' ? filters.managerId : undefined,
    seniorityLevelId: filters.seniorityLevelId !== 'all' ? filters.seniorityLevelId : undefined,
    employmentTypeId: filters.employmentTypeId !== 'all' ? filters.employmentTypeId : undefined,
    googleLinked: filters.googleLinked !== 'all' ? filters.googleLinked : undefined,
    compensationReview: filters.compensationReview !== 'all' ? filters.compensationReview : undefined,
  };

  // Query params for list view (with pagination)
  const listQueryParams: EmployeeQueryParams = { ...baseQueryParams, page };

  // Fetch employees for list view
  const { data: employeesData, isLoading: isLoadingList } = useEmployees(
    viewMode === 'list' ? listQueryParams : { limit: 1 }
  ); // Minimal query when not active

  // Fetch employees for card view (infinite scroll)
  const {
    data: infiniteData,
    isLoading: isLoadingCards,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteEmployees(viewMode === 'cards' ? baseQueryParams : { limit: 1 });

  // Flatten infinite pages into single array
  const cardEmployees = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page) => page.data);
  }, [infiniteData?.pages]);

  const listEmployees = employeesData?.data ?? [];
  const totalPages = employeesData?.meta?.totalPages ?? 0;
  // Unified total — `useEmployees` populates `meta.total` only when list-mode
  // is active; in card-mode the infinite query keeps `meta.total` on each
  // page (we read the first one). Either way we get a single number to show
  // in the breadcrumb regardless of which view the user is on.
  const total =
    viewMode === 'list'
      ? (employeesData?.meta?.total ?? 0)
      : (infiniteData?.pages?.[0]?.meta?.total ?? 0);

  // Count active filters (excluding defaults)
  const activeFilterCount = [
    filters.status !== DEFAULT_FILTERS.status && filters.status !== 'all',
    filters.departmentId !== DEFAULT_FILTERS.departmentId,
    filters.positionId !== DEFAULT_FILTERS.positionId,
    filters.positionIds && filters.positionIds.length > 0, // Category filter
    filters.managerId !== DEFAULT_FILTERS.managerId,
    filters.seniorityLevelId !== DEFAULT_FILTERS.seniorityLevelId,
    filters.employmentTypeId !== DEFAULT_FILTERS.employmentTypeId,
    // Hidden without salary.view, so don't count it.
    canViewCompensations && filters.compensationReview !== DEFAULT_FILTERS.compensationReview,
  ].filter(Boolean).length;

  // Fetch stats — category statistics (Tech, Sales, Finance, etc.) are
  // ALWAYS company-wide, showing current_staff regardless of filters. Only
  // Active / Probation counts respect filters for consistency. Stats follow
  // the same "search honors status filter" rule as the main list — typing
  // in search no longer balloons stats to include terminated employees.
  const statsParams: Omit<EmployeeQueryParams, 'page' | 'limit'> = {
    search: debouncedSearch || undefined,
    ...(filters.status === 'current_staff'
      ? { statuses: ['active', 'probation'], status: undefined }
      : filters.status !== 'all'
        ? { status: filters.status, statuses: undefined }
        : { status: undefined, statuses: undefined }),
    departmentId: filters.departmentId !== 'all' ? filters.departmentId : undefined,
    managerId: filters.managerId !== 'all' ? filters.managerId : undefined,
    seniorityLevelId: filters.seniorityLevelId !== 'all' ? filters.seniorityLevelId : undefined,
    employmentTypeId: filters.employmentTypeId !== 'all' ? filters.employmentTypeId : undefined,
    // Note: Category statistics (positions) ignore all filters and always show current_staff company-wide
  };
  const { data: stats, isLoading: isLoadingStats } = useEmployeeStats(statsParams);

  // Reset to page 1 when search or filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFiltersChange = (newFilters: EmployeeFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  return (
    <AuthGuard>
      <AppLayout>
        {/* Page Header — breadcrumb now also carries the total count so the
            duplicate "People" h1 below isn't needed (Variant D layout). */}
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span className="font-medium text-foreground">People</span>
            {total > 0 && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span className="font-mono text-xs tabular-nums">
                  {total} total
                </span>
              </>
            )}
          </div>
        </header>

        {/* Main content */}
        <div className="p-6">
          {/* Row 1 — inputs: search + create. The duplicate "People" h1 was
              dropped (breadcrumb already announces the page) and the "New"
              button moved down to share this row with search — semantic
              pairing: both are entry points for new/existing records. */}
          <div className="mb-3 flex items-center gap-3">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, email..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Row 2 — view controls: filter chips + export / advanced filter /
              column config / view-mode toggle, all inside the same bordered
              "view toolbar" card. Chips on the left, tools on the right with
              a hairline divider. */}
          <div className="mb-4">
            <StatusCards
              stats={stats}
              isLoading={isLoadingStats}
              filters={filters}
              onFilterChange={handleFiltersChange}
              actions={
                <>
                  <ExportButton queryParams={baseQueryParams} />
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setIsFilterOpen(true)}
                          className="relative rounded-full cursor-pointer"
                        >
                          <Filter className="h-4 w-4" />
                          {activeFilterCount > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-[9px] font-medium text-background">
                              {activeFilterCount}
                            </span>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Filters</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {viewMode === 'list' && (
                    <ColumnConfig
                      visibleColumns={visibleColumns}
                      onColumnsChange={setVisibleColumns}
                    />
                  )}
                  <ViewModeToggle viewMode={viewMode} onChange={handleViewModeChange} />
                  {canCreateEmployee && (
                    <>
                      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              onClick={() => setIsAddEmployeeOpen(true)}
                              className="h-9 w-9 shrink-0 rounded-full cursor-pointer"
                              aria-label="New employee"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>New employee</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </>
                  )}
                </>
              }
            />
          </div>

          {/* Filter Sidebar */}
          <FilterSidebar
            open={isFilterOpen}
            onOpenChange={setIsFilterOpen}
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />

          {/* Content based on view mode */}
          {viewMode === 'list' ? (
            <>
              {/* Employee table */}
              <div className="mb-4">
                <EmployeeTable
                  employees={listEmployees}
                  visibleColumns={visibleColumns}
                  isLoading={isLoadingList}
                  onImpersonate={canImpersonate && !isImpersonating ? handleImpersonate : undefined}
                  onDeactivate={canEditEmployee ? handleDeactivate : undefined}
                  onDelete={canDeleteEmployee ? handleDelete : undefined}
                />
              </div>

              {/* Pagination */}
              {totalPages > 0 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  limit={limit}
                  onPageChange={setPage}
                />
              )}
            </>
          ) : (
            /* Card grid with infinite scroll */
            <EmployeeCardGrid
              employees={cardEmployees}
              isLoading={isLoadingCards}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={() => fetchNextPage()}
              onImpersonate={canImpersonate && !isImpersonating ? handleImpersonate : undefined}
              onDeactivate={canEditEmployee ? handleDeactivate : undefined}
              onDelete={canDeleteEmployee ? handleDelete : undefined}
            />
          )}
        </div>

        {/* Deactivate/Reactivate Confirmation Dialog */}
        <AlertDialog
          open={deactivateDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setDeactivateDialog({
                open: false,
                employeeId: null,
                employeeName: '',
                isReactivate: false,
              });
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deactivateDialog.isReactivate ? 'Reactivate Employee' : 'Deactivate Employee'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deactivateDialog.isReactivate ? (
                  <>
                    Are you sure you want to reactivate{' '}
                    <strong>{deactivateDialog.employeeName}</strong>? Their status will be changed
                    to active and termination date will be cleared.
                  </>
                ) : (
                  <>
                    Are you sure you want to deactivate{' '}
                    <strong>{deactivateDialog.employeeName}</strong>? Their status will be changed
                    to terminated. This action cannot be undone.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeactivate}
                variant={deactivateDialog.isReactivate ? 'default' : 'destructive'}
                className={
                  deactivateDialog.isReactivate
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : undefined
                }
              >
                {deactivateDialog.isReactivate ? 'Reactivate' : 'Deactivate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reassign direct reports before a manager can be deactivated */}
        {reportHandoff && (
          <ReassignReportsDialog
            open
            onOpenChange={(open) => {
              if (!open) setReportHandoff(null);
            }}
            employeeId={reportHandoff.employeeId}
            employeeName={reportHandoff.employeeName}
            reports={reportHandoff.reports}
            onConfirm={confirmReportHandoff}
            isSubmitting={isHandingOff}
          />
        )}

        {/* Add Employee Dialog */}
        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={deleteDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteDialog({ open: false, employeeId: null, employeeName: '' });
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Employee</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to permanently delete{' '}
                <strong>{deleteDialog.employeeName || 'this employee'}</strong>? This action cannot
                be undone and will remove all employee data from the database.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} variant="destructive">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AddEmployeeDialog
          open={isAddEmployeeOpen}
          onOpenChange={setIsAddEmployeeOpen}
          onSuccess={(employeeId: string) => {
            navigate({ to: '/employees/$id', params: { id: employeeId } });
          }}
        />
      </AppLayout>
    </AuthGuard>
  );
}

// ---------------------------------------------------------------------------
// ViewModeToggle — segmented list/cards switcher used inside the StatusCards
// `actions` slot. Lifted into its own component so the inline render of the
// People route stays focused on layout composition rather than JSX details.
// ---------------------------------------------------------------------------
function ViewModeToggle({
  viewMode,
  onChange,
}: {
  viewMode: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-center rounded-full border border-border bg-muted/30 p-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 rounded-full cursor-pointer',
                viewMode === 'list' && 'bg-background shadow-sm'
              )}
              onClick={() => onChange('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">List view</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 rounded-full cursor-pointer',
                viewMode === 'cards' && 'bg-background shadow-sm'
              )}
              onClick={() => onChange('cards')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Card view</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
